import { sanitize } from "dompurify";
import React, { useState, useEffect, useRef } from "react";

interface TypewriterEffectProps {
    text: string;
    typingSpeed?: number;
    onComplete?: () => void;
    className?: string;
}

export default function TypewriterEffect({
    text,
    typingSpeed = 12,
    onComplete,
    className = "",
}: TypewriterEffectProps) {
    const [displayedText, setDisplayedText] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isComplete, setIsComplete] = useState(false);

    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (currentIndex < text.length && !isComplete) {
            timeoutRef.current = window.setTimeout(() => {
                setDisplayedText(text.slice(0, currentIndex + 1));
                setCurrentIndex(currentIndex + 1);
            }, typingSpeed);
        } else if (currentIndex >= text.length && !isComplete) {
            setIsComplete(true);
            if (onComplete) {
                onComplete();
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [currentIndex, text, typingSpeed, isComplete, onComplete]);

    useEffect(() => {
        setDisplayedText("");
        setCurrentIndex(0);
        setIsComplete(false);
    }, [text]);

    return (
        <span
            className={`inline-flex items-center whitespace-pre-wrap ${className}`}
        >
            {sanitize(displayedText)}
        </span>
    );
}
